import { useEffect, useMemo, useState } from 'react';
import { Edit, Trash2, Search, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmationModal from '../common/ConfirmationModal';
import useConfirmation from '../../hooks/useConfirmation';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { useNavigate } from 'react-router-dom';
import api from '../../services/apiClient.ts';
import { formatDate } from '../../utils/date';

const TENANT_ID = 'default-tenant-1';
const HEADERS = { 'X-Tenant': TENANT_ID };

const ManageGroups = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [idSearch, setIdSearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { confirmationState, hideConfirmation, confirmDelete } = useConfirmation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadGroups = async () => {
      setIsLoading(true);
      try {
        // المجموعات + عدد المسافرين + إجمالي الإيرادات/الصرفيات (مسافرين + عامة)
        const [groupsRes, detailsRes, revExpRes] = await Promise.all([
          api.get('/v1/groups', { params: { per_page: 500 }, headers: HEADERS }),
          api.get('/v1/group-details', { params: { per_page: 2000 }, headers: HEADERS }),
          api.get('/v1/reports/group-revenues-expenses', { headers: HEADERS }),
        ]);

        const groupsData  = groupsRes.data?.data  || groupsRes.data  || [];
        const detailsData = detailsRes.data?.data || detailsRes.data || [];
        const revExpData  = revExpRes.data?.data  || [];

        const detailsByGroup = new Map();
        for (const d of detailsData) {
          const key = Number(d.gr_id);
          if (!detailsByGroup.has(key)) detailsByGroup.set(key, []);
          detailsByGroup.get(key).push(d);
        }

        const revExpByGroup = new Map();
        for (const r of (Array.isArray(revExpData) ? revExpData : [])) {
          revExpByGroup.set(Number(r.group_id), {
            totalRevenue: parseFloat(r.total_revenue) || 0,
            totalExpenses: parseFloat(r.total_expenses) || 0,
          });
        }

        const computed = groupsData.map((group) => {
          const members = detailsByGroup.get(Number(group.id)) || [];
          const memberCount = members.length;
          const finance = revExpByGroup.get(Number(group.id));
          // نفس منطق صفحة group-revenues-expenses
          const totalAmount = finance
            ? finance.totalRevenue
            : members.reduce((sum, d) => sum + (parseFloat(d.total_price) || 0), 0);
          const totalExpenses = finance ? finance.totalExpenses : 0;
          const planDate = group.plan_start_date || members[0]?.plan_start_date || group.created_at;
          const dateStr = formatDate(planDate);

          return {
            id: group.id,
            groupName: group.gr_notes || `${t('home.group')} ${group.id}`,
            date: dateStr,
            memberCount,
            totalAmount,
            totalExpenses,
          };
        });

        setGroups(computed);
      } catch (err) {
        console.error('Failed to load groups:', err);
        toast.error(t('common.errorLoadingData') || 'خطأ في تحميل بيانات المجموعات');
      } finally {
        setIsLoading(false);
      }
    };

    loadGroups();
  }, [t]);

  const filteredGroups = useMemo(() => {
    const textQuery = search.trim().toLowerCase();
    const idQuery = idSearch.trim();

    if (!textQuery && !idQuery) return groups;

    return groups.filter((g) => {
      const matchesText = !textQuery
        || (g.groupName || '').toLowerCase().includes(textQuery)
        || (g.date || '').includes(search);

      const matchesId = !idQuery || String(g.id).includes(idQuery);

      return matchesText && matchesId;
    });
  }, [groups, search, idSearch]);

  // Progressive client-side loading: render rows in chunks as the user scrolls.
  const {
    visibleItems: visibleGroups,
    hasMore,
    scrollRef,
    sentinelRef,
  } = useInfiniteScroll(filteredGroups, { pageSize: 30 });

  const handleEdit = (id) => navigate(`/groups/${id}/edit`);

  const handleDelete = (id) => {
    const group = groups.find(g => g.id === id);
    confirmDelete(group?.groupName || t('groups.title'), async () => {
      try {
        await api.delete(`/v1/groups/${id}`, { headers: HEADERS });
        setGroups(prev => prev.filter(g => g.id !== id));
        toast.success(t('groups.deleteSuccess'));
      } catch (err) {
        console.error('Delete group failed:', err);
        toast.error(t('groups.deleteError') || 'فشل حذف المجموعة');
      }
    });
  };

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-reverse space-x-3">
                <div className="bg-white/20 rounded-full p-3">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{t('groups.title')}</h2>
                  <p className="text-blue-100 mt-1">{t('groups.subtitleManage')}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 w-full sm:w-auto sm:flex-nowrap">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 text-white/80 h-5 w-5" />
                  <input
                    type="text"
                    placeholder={t('groups.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                  />
                </div>
                <div className="relative w-full sm:w-44">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="بحث بالرقم"
                    value={idSearch}
                    onChange={(e) => setIdSearch(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="p-4 sm:p-6">
            <div ref={scrollRef} className="app-table-wrap">
              <table className="app-table">
                <thead>
                  <tr>
                    <th className="text-center w-16">ID</th>
                    <th className="text-center w-24">{t('groups.actions')}</th>
                    <th className="text-right">{t('groups.groupName')}</th>
                    <th className="text-right">{t('groups.tripStartDate')}</th>
                    <th className="text-center w-16 whitespace-nowrap">المسافرين</th>
                    <th className="text-right">{t('groups.totalAmount')}</th>
                    <th className="text-right">إجمالي الصرفيات</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>
                        <td className="text-center"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-10 mx-auto" /></td>
                        <td><div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16 mx-auto" /></td>
                        <td className="text-right"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-40 ml-auto rtl:ml-0 rtl:mr-auto" /></td>
                        <td className="text-right"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24 ml-auto rtl:ml-0 rtl:mr-auto" /></td>
                        <td className="text-center"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-6 mx-auto" /></td>
                        <td className="text-right"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-28 ml-auto" /></td>
                        <td className="text-right"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24 ml-auto" /></td>
                      </tr>
                    ))
                  ) : filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                        {t('groups.noGroupsFound')}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {visibleGroups.map((group) => (
                        <tr key={group.id}>
                          <td className="text-center font-semibold text-gray-700 dark:text-gray-300">{group.id}</td>
                          <td className="text-center w-24">
                            <div className="flex justify-center gap-2 rtl:flex-row-reverse">
                              <button
                                onClick={() => handleEdit(group.id)}
                                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 hover:scale-105"
                                title={t('common.edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(group.id)}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 hover:scale-105"
                                title={t('common.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="text-right">
                            <span className="text-base font-medium text-gray-900 dark:text-gray-100">{group.groupName}</span>
                          </td>
                          <td className="text-right text-gray-600 dark:text-gray-400">{group.date}</td>
                          <td className="text-center font-medium w-16">{group.memberCount}</td>
                          <td className="text-right font-semibold text-gray-900 dark:text-gray-100">
                            {group.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                          </td>
                          <td className="text-right font-semibold text-red-600 dark:text-red-400">
                            {group.totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                          </td>
                        </tr>
                      ))}
                      {hasMore && (
                        <tr ref={sentinelRef}>
                          <td colSpan={7} className="px-6 py-4 text-center text-xs text-gray-400 dark:text-gray-500">
                            {t('common.loadingMore') || '...'}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            {!isLoading && filteredGroups.length > 0 && (
              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                {visibleGroups.length} / {filteredGroups.length}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={confirmationState.open}
        onClose={hideConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        type={confirmationState.type}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        confirmColor={confirmationState.confirmColor}
      />
    </div>
  );
};

export default ManageGroups;
